import { Button, Spacer } from '@odekoteam/doppio';
import { useState } from 'react';

import { useAppDispatch, useAppSelector } from '../../hooks/store';
import {
  isSelectedNightEndBeforeWarehouseNightEnd,
  nightEndSelector,
  selectedNightDaysPastSelector,
  selectedWarehouseIdSelector,
} from '../../state/selectors/delivery';
import { modalSelector } from '../../state/selectors/modal';
import { markCustomerPickupStopsComplete } from '../../state/slices/delivery';
import { hideModal } from '../../state/slices/modal';
import CancelButton from './CancelButton';
import { buttonContainer, styledConfirmationModal } from './styles';

// Copy is the design's (sc-65865): a real title, the actual consequence, and a
// named action button rather than "Yes". Completion delivers each order at
// fulfilled quantity, so there is nothing to undo afterwards.
export const MARK_PICKED_UP_TITLE = 'Mark as picked up?';
export const MARK_PICKED_UP_BODY =
  "This can't be undone. The customer will be invoiced on the next billing cycle.";

// The review sweep auto-confirms only orders delivered within 14 days, and
// completion records delivery on the night itself. One day of margin covers
// the sweep window's wall-clock edge.
const PAST_NIGHT_REVIEW_DAYS = 13;

export const pastNightPickedUpLine = (daysPast: number): string =>
  `This night has already ended, so the pickup is recorded on that night. ${
    daysPast >= PAST_NIGHT_REVIEW_DAYS
      ? "It won't be confirmed for billing automatically: review it in Invoice by Delivery."
      : 'It will be confirmed for billing at the next review sweep.'
  }`;

// Shared by both pickup confirmations: the line, or nothing on a night that
// has not ended.
export function PastNightPickedUpLine(): JSX.Element | null {
  const isPastNight = useAppSelector(isSelectedNightEndBeforeWarehouseNightEnd);
  const daysPast = useAppSelector(selectedNightDaysPastSelector);
  if (!isPastNight) return null;
  return (
    <>
      <Spacer height="$2" />
      <div
        data-test-id="past-night-picked-up-line"
        sx={{ variant: 'text.paragraphMdDefault', color: 'secondaryText' }}
      >
        {pastNightPickedUpLine(daysPast)}
      </div>
    </>
  );
}

export function MarkPickedUpModal(): JSX.Element {
  const dispatch = useAppDispatch();
  const nightEnd = useAppSelector(nightEndSelector);
  const { variables } = useAppSelector(modalSelector);
  const selectedWarehouseId = useAppSelector(selectedWarehouseIdSelector);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const stopIds: string[] = variables?.stopIds ?? [];

  const handleOnClickConfirm = async (): Promise<void> => {
    if (stopIds.length === 0 || !selectedWarehouseId || !nightEnd?.viewing) {
      dispatch(hideModal());
      return;
    }
    setIsSubmitting(true);
    await dispatch(
      markCustomerPickupStopsComplete({
        stopIds,
        warehouseId: selectedWarehouseId,
        nightEnd: nightEnd.viewing,
      }),
    );
    setIsSubmitting(false);
    dispatch(hideModal());
  };

  return (
    <div
      id="mark-picked-up-modal"
      sx={{ ...styledConfirmationModal, textAlign: 'left' }}
    >
      <div sx={{ variant: 'text.headingSm' }}>{MARK_PICKED_UP_TITLE}</div>
      <Spacer height="$3" />
      <div sx={{ variant: 'text.paragraphMdDefault', color: 'secondaryText' }}>
        {MARK_PICKED_UP_BODY}
      </div>
      <PastNightPickedUpLine />
      <div sx={{ ...buttonContainer, justifyContent: 'flex-start' }}>
        <Button
          variant="primary"
          id="mark-picked-up-action-button"
          onPress={handleOnClickConfirm}
          isDisabled={isSubmitting}
        >
          {isSubmitting ? 'Marking...' : 'Mark as picked up'}
        </Button>
        <Spacer width="$2" />
        <CancelButton variant="secondary" />
      </div>
    </div>
  );
}
